/**
 * Local hot-seat game (no backend).
 *
 * Drives the pure rules engine from React state. Used for pass-the-device
 * play and as the engine's end-to-end harness without a Convex deployment.
 *
 * Scoring follows the ahwa convention: the losing side adds penalty points —
 * in 2v2 the losing TEAM takes ALL pips left on the table, every hand
 * included, the winners' side too (ADR-0019) — and the first side to reach
 * the target loses the match. Every round is opened by the highest double's
 * holder leading that double (ADR-0019); scores for the finished round are
 * derived live (so the round-over panel can fill the glasses) and committed
 * when the next round starts.
 */

import type { End, GameMode, RoundState } from "@domino/types";
import { makeRulesSnapshot, seatsForSide } from "@domino/types";
import {
  applyDraw,
  applyPass,
  applyPlay,
  projectToPlayerView,
  startRound,
} from "@domino/rules-engine";
import { useCallback, useMemo, useState } from "react";

function randomSeed(): number {
  return Math.floor(Math.random() * 2 ** 31);
}

/** Penalty each side collects from the finished round (0 for winners/ties). */
export function penaltiesFor(state: RoundState, mode: GameMode): number[] {
  const result = state.result;
  const sides = mode === "3p" ? 3 : 2;
  if (!result || result.winningSide === null) return new Array(sides).fill(0);
  const winnerSides = new Set<number>(
    mode === "2v2" ? [result.winningSide] : (result.winners as number[]),
  );
  // ADR-0019 (2v2): the losing team takes ALL pips — winners' hands included.
  const totalPips = Object.values(result.pipCounts).reduce((sum, v) => sum + v, 0);
  return Array.from({ length: sides }, (_, side) => {
    if (winnerSides.has(side)) return 0;
    if (mode === "2v2") return totalPips;
    return seatsForSide(mode, side).reduce(
      (sum, seat) => sum + (result.pipCounts[String(seat)] ?? 0),
      0,
    );
  });
}

export function useLocalGame(mode: GameMode, targetScore = 100) {
  const rules = useMemo(() => makeRulesSnapshot(mode, { targetScore }), [mode, targetScore]);
  const [state, setState] = useState<RoundState>(() => startRound(rules, randomSeed(), 1, true).state);
  const [committedScores, setCommittedScores] = useState<number[]>(() =>
    new Array(mode === "3p" ? 3 : 2).fill(0),
  );

  const view = useMemo(() => projectToPlayerView(state, state.currentSeat), [state]);

  // Live scoreboard: committed + current round's penalties once it ends.
  const scores = useMemo(() => {
    const penalties = penaltiesFor(state, mode);
    return committedScores.map((s, i) => s + (penalties[i] ?? 0));
  }, [committedScores, state, mode]);

  const play = useCallback((tileId: number, end?: End) => {
    setState((current) => {
      if (current.status !== "active") return current;
      try {
        return applyPlay(current, current.currentSeat, tileId, end).state;
      } catch {
        return current;
      }
    });
  }, []);

  const draw = useCallback(() => {
    setState((current) => {
      if (current.status !== "active") return current;
      try {
        return applyDraw(current, current.currentSeat).state;
      } catch {
        return current;
      }
    });
  }, []);

  const pass = useCallback(() => {
    setState((current) => {
      if (current.status !== "active") return current;
      try {
        return applyPass(current, current.currentSeat).state;
      } catch {
        return current;
      }
    });
  }, []);

  const nextRound = useCallback(() => {
    setState((current) => {
      if (!current.result) return current;

      // Commit this round's penalties into the match tally.
      const penalties = penaltiesFor(current, mode);
      setCommittedScores((prev) => prev.map((s, i) => s + (penalties[i] ?? 0)));

      // ADR-0019: every round opens by the highest-double holder leading it —
      // startRound resolves that seat from the fresh deal; the previous
      // winner no longer opens.
      const started = startRound(rules, randomSeed(), current.roundNumber + 1, false);
      return started.state;
    });
  }, [mode, rules]);

  const reset = useCallback(() => {
    setState(startRound(rules, randomSeed(), 1, true).state);
    setCommittedScores(new Array(mode === "3p" ? 3 : 2).fill(0));
  }, [mode, rules]);

  // Match result reads from the live tally (what the players see).
  const matchFinished = scores.some((s) => s >= targetScore);
  const matchLoser = matchFinished ? scores.findIndex((s) => s >= targetScore) : null;
  const matchWinner = matchFinished
    ? scores.indexOf(Math.min(...scores.filter((_, i) => i !== matchLoser)))
    : null;

  return {
    state,
    view,
    rules,
    play,
    draw,
    pass,
    nextRound,
    reset,
    scores,
    targetScore,
    matchFinished,
    matchWinner,
    matchLoser,
  };
}
